/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { ROUTE_PATHS } from 'routes';

import Button from 'components/atoms/Button';
import SectionTitle from 'components/atoms/SectionTitle';
import BannerCarousel from 'components/molecules/BannerCarousel';
import Coupon, { ColorStyle } from 'components/molecules/Coupon';
import ProductListCarousel from 'components/molecules/ProductListCarousel';
import ContactUs from 'components/molecules/ContactUs';
import ProductCard from 'components/molecules/ProductCard';
import CouponCarousel from 'components/molecules/CouponCarousel';
import CommentsCarousel from 'components/organisms/CommentsCarousel';
import ProductFeatureTab from 'components/organisms/ProductFeatureTab';
import HomeNews from 'components/organisms/HomeNews';
import Link from 'components/atoms/Link';

import { useFlashSales } from 'models/flashSales';
import { useVideos } from 'models/vedio';
import { useBanner } from 'models/banner';
import { useProducts } from 'models/products';
import { useCoupon } from 'models/coupon';
import { useBlog, useNewsList } from 'models/blog';
import { useHistory } from 'models/routing';
import { useMember } from 'models/member';
import { useRedirect } from 'models/redirect';

import { ImpressionsProps } from 'types/impressions';
import { MemberFeatureType } from 'enums/memberFeatureType';

import { omniEvent } from 'util/omniEvent';
import useMedia from 'util/hook/useMedia';
import { removeItem } from 'util/storage';

import SignUpGiftModal from 'components/molecules/SignUpGiftModal';
import SEOHelmet from 'components/atoms/SEOHelmet';
import pushHistory from 'util/pushHistory';
import YoutubePlayer from 'components/atoms/YoutubePlayer';
import { CommentsData } from './config';

import styles from './index.module.css';

const CouponCardColorStyle: ColorStyle[] = ['purple', 'pink', 'green'];

/**
 * 首頁
 *
 */
const Home: React.FC = () => {
	const media = useMedia();
	const [{ flashSales }] = useFlashSales();
	const [{ videos }] = useVideos();
	const [{ banners }] = useBanner();
	const [{ exclusiveSale, indexSeries }] = useProducts();
	const [{ couponsList }] = useCoupon();
	const [{ newsCategories }] = useBlog();
	const [{ newsList }] = useNewsList();
	const [{ userInfo }] = useMember();
	const history = useHistory();
	const [, { pushAuthGuardRoute }] = useRedirect();

	const youtubeListId = useMemo(() => {
		if (videos[0] && videos[0].youtubeLink) {
			const urlObject = new URL(videos[0].youtubeLink);
			const { searchParams } = urlObject;
			return searchParams.get('list') || '';
		}
		return '';
	}, [videos]);

	const couponCardDataList = useMemo(
		() =>
			couponsList.map((item, index) => ({ ...item, colorStyle: CouponCardColorStyle[index % 3] })),
		[couponsList],
	);

	const clickSeeAllCoupon = () => {
		const path = `/${ROUTE_PATHS.memberCentre}?type=${MemberFeatureType.COUPONS}`;
		pushAuthGuardRoute(path);
	};

	useEffect(() => {
		const impressions: ImpressionsProps[] = [];
		exclusiveSale.forEach((p, i) => {
			impressions.push({
				name: p.name!, // Name is required.
				id: p.materialNo!, // ID is required.
				price: p.memberPrice || p.eventPrice!,
				brand: '愛康生技',
				category: '', // 待後端
				variant: p.briefDescription!,
				position: i,
			});
		});

		omniEvent(null); // Clear the previous i13n object.
		omniEvent({
			action: 'ViewContent',
			p: '首頁', // Page Name
			uid: userInfo.id ?? '', //  User ID is required if user has login
			currencyCode: 'TWD',
			impressions,
		});
	}, [exclusiveSale]);

	useEffect(() => {
		removeItem('breadcrumb');
	}, []);

	return (
		<main className={styles.home}>
			<SignUpGiftModal />
			<SEOHelmet img={banners.length ? banners[0].image : ''} />
			{/* Banner 輪播 */}
			{banners.length > 0 && (
				<section className={styles.bannerSection}>
					<BannerCarousel data={banners} />
				</section>
			)}
			{/* 限時活動 */}
			{flashSales[0] && (
				<section className={classnames(styles.flashSaleSection, styles.section)}>
					<SectionTitle chName="限時活動" enName="FLASH SALE" className={styles.sectionTitle} />
					<div className={styles.saleContainer}>
						<Link
							className={styles.flashSaleLink}
							type="external"
							to={flashSales[0].imageFirstLink}
							showPointer={!!flashSales[0].imageFirstLink}
						>
							<img src={flashSales[0].imageFirst} alt={flashSales[0].imageFirstAltText} />
						</Link>
						<Link
							className={styles.flashSaleLink}
							type="external"
							to={flashSales[0].imageSecondLink}
							showPointer={!!flashSales[0].imageSecondLink}
						>
							<img src={flashSales[0].imageSecond} alt={flashSales[0].imageSecondAltText} />
						</Link>
					</div>
				</section>
			)}

			{/* 優惠券，排序：紫>粉>綠 */}
			{couponsList.length > 0 && (
				<section className={classnames(styles.couponSection, styles.section)}>
					<SectionTitle chName="優惠券" enName="COUPON" className={styles.sectionTitle} />
					{media === 'desktop' ? (
						<CouponCarousel
							className={styles.couponCarousel}
							data={couponCardDataList}
							clickCoupon={clickSeeAllCoupon}
						/>
					) : (
						<div className={styles.couponRwdWrapper}>
							{couponCardDataList.map(
								({
									title = '',
									subTitle = '',
									hashtag = '',
									colorStyle = 'purple',
									briefDescription = '',
								}) => (
									<Coupon
										title={title}
										subtitle={subTitle}
										hashtag={hashtag}
										colorStyle={colorStyle}
										briefDescription={briefDescription}
										className={styles.smallCoupon}
										onClick={clickSeeAllCoupon}
									/>
								),
							)}
						</div>
					)}
					<Button
						name="查看全部"
						pattern="primaryOutline"
						size="large"
						handleClick={clickSeeAllCoupon}
						Icon="arrow"
						className={styles.button}
					/>
				</section>
			)}

			{/* 官網獨家優惠 */}
			{exclusiveSale.length > 0 && (
				<section className={classnames(styles.specialDiscountSection, styles.section)}>
					<SectionTitle chName="官網獨家優惠" enName="EXCLUSIVE" className={styles.sectionTitle} />
					<div className={styles.specialDiscountContainer}>
						{media === 'desktop' && <ProductListCarousel data={exclusiveSale} pageName="首頁" />}
						{media === 'mobile' &&
							exclusiveSale.map((item, index) => (
								<ProductCard {...item} key={item.name} index={index} pageName="首頁" />
							))}
					</div>
					<Button
						size="large"
						pattern="primaryOutline"
						name="查看全部"
						handleClick={() => {
							pushHistory(history, '/products?all=1');
							window.scrollTo(0, 0);
						}}
						Icon="arrow"
					/>
				</section>
			)}

			{/* 影片 */}
			{youtubeListId && (
				<section className={classnames(styles.videoSection, styles.section)}>
					<div className={styles.videoWrapper}>
						<YoutubePlayer
							title={videos[0] ? videos[0].title || '' : 'youtube video'}
							videoListLink={videos[0] ? videos[0].youtubeLink || '' : ''}
						/>
					</div>
				</section>
			)}

			{/* 商品系列特色 */}
			{indexSeries.length > 0 && (
				<section className={classnames(styles.featureSection, styles.section)}>
					<SectionTitle chName="商品特色" enName="SERIES" className={styles.sectionTitle} />
					<ProductFeatureTab className={styles.productFeatureTab} />
				</section>
			)}

			{/* 最新消息 */}
			{newsCategories.length && !Object.values(newsList).every(i => i?.length === 0) && (
				<section className={classnames(styles.newsSection, styles.section)}>
					<SectionTitle chName="最新消息" enName="NEWS" className={styles.sectionTitle} />
					<HomeNews
						data={newsList}
						className={styles.tab}
						tabsData={newsCategories.map((category, index) => ({
							label: category.name!,
							index,
							id: category?.id || index,
						}))}
					/>
				</section>
			)}

			{/* 用戶好評 */}
			<section className={classnames(styles.reviewSection, styles.section)}>
				<SectionTitle chName="用戶好評" enName="REVIEWS" className={styles.sectionTitle} />
				<CommentsCarousel data={CommentsData} />
			</section>

			{/* 聯絡我們 */}
			<ContactUs className={styles.contactUs} />
		</main>
	);
};

export default Home;
